/**
 * 動画入稿のブラウザ側チェック。
 *
 * サーバー（POST /admin/uploads/video）でも同じ内容を実バイトで検査しているので、
 * ここを通らなくても壊れた動画が入ることはない。ここでやる意味は
 * 「100MB を送りつけてから怒られる」のを避けることと、原因をその場で示すこと。
 *
 * ffmpeg 相当のものは使わない。必要な判定は以下だけで、いずれも
 * <video> 要素と先頭数十 KB の走査で足りる。
 */

/** 比率の許容誤差。これを超えたら警告する（cover で表示するので切れるだけ） */
export const ASPECT_RATIO_TOLERANCE = 0.02;
/** サーバー側の multer 上限と揃えること */
export const VIDEO_FILE_SIZE_LIMIT = 100 * 1024 * 1024;
export const VIDEO_SUPPORTED_FORMATS = ['video/mp4'];

/**
 * 表示面ごとの入稿条件。
 *
 * mp4 か / faststart か / 壊れていないか はどの面でも同じなので共通のまま。
 * 一方で「何秒までか」「どの比率か」は用途で変わる。待機画面の添え物と
 * 課題の解説動画を同じ条件で弾くと、正しい素材が入らなくなる。
 */
export interface VideoValidationProfile {
  /** 表示枠の縦横比。ずれていたら警告（入稿は止めない） */
  aspectRatio: number;
  /** 推奨する尺の上限。超えたら警告 */
  recommendedMaxDurationSec: number;
  /** この尺を超えたら入稿させない */
  hardMaxDurationSec: number;
  /** 推奨解像度。警告文に出すだけ */
  recommendedResolution: { width: number; height: number };
  /** 警告文で使う比率の呼び名（例: 4:5） */
  aspectRatioLabel: string;
  /** 比率がずれたときに何が起きるかの一文 */
  aspectMismatchNote: string;
  /** 尺が推奨を超えたときの一文 */
  durationNote: string;
}

/** 日記の生成待ち画面。20〜40 秒しか出ない 4:5 の添え物 */
export const SCREEN_MEDIA_VIDEO_PROFILE: VideoValidationProfile = {
  aspectRatio: 4 / 5,
  recommendedMaxDurationSec: 40,
  hardMaxDurationSec: 120,
  recommendedResolution: { width: 1080, height: 1350 },
  aspectRatioLabel: '4:5',
  aspectMismatchNote:
    '表示枠に合わせて中央を切り抜いて表示されるため、端が見切れます。',
  durationNote:
    '待機画面は 20〜40 秒ほどしか表示されないため、最後まで見られない可能性があります。',
};

/**
 * あゆみの導入ボトムシート。
 *
 * 課題の解説動画と違い枠が縦長（Figma 実測 327×408）。初回に一度だけ出るもの
 * なので、長すぎると離脱される。課題動画より短めを推奨にする。
 */
export const AYUMI_INTRO_VIDEO_PROFILE: VideoValidationProfile = {
  aspectRatio: 327 / 408,
  recommendedMaxDurationSec: 90,
  hardMaxDurationSec: 300,
  recommendedResolution: { width: 1080, height: 1350 },
  aspectRatioLabel: '4:5（縦）',
  aspectMismatchNote: '表示枠は縦長です。比率が違うと余白が出ます。',
  durationNote:
    '導入で一度だけ出るものなので、長いと最後まで観られない可能性があります。',
};

/**
 * あゆみの課題解説動画。
 *
 * 待機画面と違い、ユーザーがタップして最後まで観る想定なので尺の上限を緩くとる。
 * 16:9 なのは「課題を知る」が全幅 375×211、家族トークが 304×171 のため。
 */
export const AYUMI_VIDEO_PROFILE: VideoValidationProfile = {
  aspectRatio: 16 / 9,
  recommendedMaxDurationSec: 300,
  hardMaxDurationSec: 900,
  recommendedResolution: { width: 1920, height: 1080 },
  aspectRatioLabel: '16:9',
  aspectMismatchNote:
    '表示枠は 16:9 です。比率が違うと上下または左右に余白が出ます。',
  durationNote:
    '5 分を超える動画は最後まで観られにくくなります。尺を見直せないか検討してください。',
};

export interface VideoValidationResult {
  /** 入稿を止める理由。空なら進められる */
  errors: string[];
  /** 止めはしないが運用者に伝えるべきこと */
  warnings: string[];
  durationSec: number | null;
  width: number | null;
  height: number | null;
  /** 1 フレーム目から作ったポスター画像。動画と同じ比率 */
  posterFile: File | null;
}

/** mp4 の box ヘッダは size(4) + type(4) */
const BOX_HEADER_SIZE = 8;
/** faststart 判定に必要なのは先頭の数 box だけ */
const HEADER_SCAN_BYTES = 64 * 1024;

interface TopLevelBox {
  type: string;
  size: number;
}

function readTopLevelBoxes(buffer: ArrayBuffer): TopLevelBox[] {
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);
  const boxes: TopLevelBox[] = [];
  let offset = 0;

  while (offset + BOX_HEADER_SIZE <= buffer.byteLength) {
    let size = view.getUint32(offset);
    let headerSize = BOX_HEADER_SIZE;

    if (size === 1) {
      if (offset + 16 > buffer.byteLength) break;
      // 64bit サイズ。上位 32bit は実運用では 0
      size =
        view.getUint32(offset + 8) * 0x100000000 + view.getUint32(offset + 12);
      headerSize = 16;
    }

    let type = '';
    for (let i = offset + 4; i < offset + BOX_HEADER_SIZE; i += 1) {
      type += String.fromCharCode(bytes[i]);
    }
    boxes.push({ type, size });

    // size 0 は「ファイル末尾まで」。それ以上たどれない
    if (size === 0 || size < headerSize) break;

    offset += size;
  }

  return boxes;
}

function readHead(file: File, bytes: number): Promise<ArrayBuffer> {
  return file.slice(0, bytes).arrayBuffer();
}

/**
 * moov が mdat より前にあるか。
 *
 * moov が末尾にあると、プレイヤーはファイル全体をダウンロードし終えるまで
 * 再生を始められない。待機画面は 20〜40 秒しか出ないので致命的になる。
 * 先頭の box 並びを見るだけで判定できるので、アップロード前に止められる。
 */
export async function isFaststartMp4(file: File): Promise<boolean> {
  const head = await readHead(file, HEADER_SCAN_BYTES);
  const boxes = readTopLevelBoxes(head);
  const moovIndex = boxes.findIndex((b) => b.type === 'moov');
  const mdatIndex = boxes.findIndex((b) => b.type === 'mdat');

  if (moovIndex < 0) {
    // 先頭 64KB に moov が無い = 後ろにある
    return false;
  }

  return mdatIndex < 0 || moovIndex < mdatIndex;
}

interface LoadedVideo {
  video: HTMLVideoElement;
  objectUrl: string;
}

function loadVideoMetadata(file: File): Promise<LoadedVideo> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement('video');

    video.preload = 'metadata';
    video.muted = true;
    // 1 フレーム目を canvas に描くために必要（CORS 汚染は blob なので起きない）
    video.playsInline = true;

    const cleanupAndReject = (message: string) => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(message));
    };

    video.onloadedmetadata = () => resolve({ video, objectUrl });
    // H.265(HEVC) / AV1 はブラウザによってここで失敗する。アプリ側でも
    // 再生できない形式なので、真っ先に疑うべき原因として挙げておく。
    video.onerror = () =>
      cleanupAndReject(
        'この動画をブラウザで読み込めませんでした。H.265(HEVC) や AV1 で書き出されているか、ファイルが壊れている可能性があります。H.264 で書き出し直してください。',
      );

    video.src = objectUrl;
  });
}

/**
 * 1 フレーム目を JPEG にしてポスター画像として返す。
 *
 * 運用者にポスター画像を別途用意させると必ず忘れられるうえ、
 * 動画の 1 フレーム目以外を指定されると再生開始の瞬間に絵が飛ぶ。
 * 自動生成なら「何もしなくても継ぎ目がない」状態になる。
 */
function capturePoster(
  video: HTMLVideoElement,
  baseName: string,
): Promise<File | null> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (file: File | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve(file);
    };

    // seeked が来ないまま止まる経路がある（duration が 0 / NaN、デコード待ちなど）。
    // ポスターが無くても入稿自体は続けられるので、待ち続けずに諦める。
    const timer = window.setTimeout(() => finish(null), 5000);

    const draw = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const context = canvas.getContext('2d');
        if (!context) return finish(null);

        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) =>
            finish(
              blob
                ? new File([blob], `${baseName}-poster.jpg`, {
                    type: 'image/jpeg',
                  })
                : null,
            ),
          'image/jpeg',
          0.9,
        );
      } catch {
        // ポスターが作れなくても入稿自体は続けられるようにする
        finish(null);
      }
    };

    video.onseeked = draw;
    video.onerror = () => finish(null);

    // HAVE_CURRENT_DATA 以上ならそのまま描ける
    if (video.readyState >= 2) {
      draw();

      return;
    }

    // シークでフレームを読み込ませる。currentTime が動かないと seeked が
    // 来ないので、0 のままになる値は指定しない。
    const seekTo = Number.isFinite(video.duration)
      ? Math.min(0.01, video.duration / 2)
      : 0.01;

    if (seekTo <= 0) {
      finish(null);

      return;
    }

    video.currentTime = seekTo;
  });
}

function formatBytes(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
}

/**
 * アップロード前に動画を検査し、ポスター画像も作る。
 *
 * profile は表示面ごとの条件。既定は日記の生成待ち画面（最初にこの仕組みを
 * 入れた面）で、あゆみなど別の面から呼ぶときは明示的に渡す。
 */
export async function validateVideoFile(
  file: File,
  profile: VideoValidationProfile = SCREEN_MEDIA_VIDEO_PROFILE,
): Promise<VideoValidationResult> {
  const result: VideoValidationResult = {
    errors: [],
    warnings: [],
    durationSec: null,
    width: null,
    height: null,
    posterFile: null,
  };

  if (!VIDEO_SUPPORTED_FORMATS.includes(file.type)) {
    result.errors.push('mp4 形式の動画を選択してください。');

    return result;
  }

  if (file.size > VIDEO_FILE_SIZE_LIMIT) {
    result.errors.push(
      `ファイルサイズが ${formatBytes(file.size)} です。${formatBytes(
        VIDEO_FILE_SIZE_LIMIT,
      )} 以内にしてください。`,
    );

    return result;
  }

  if (!(await isFaststartMp4(file))) {
    result.errors.push(
      '再生開始までに動画全体のダウンロードが必要な形式です。書き出し設定で faststart（Web 用に最適化 / ストリーミング配信用）を有効にして書き出し直してください。',
    );

    return result;
  }

  let loaded: LoadedVideo;
  try {
    loaded = await loadVideoMetadata(file);
  } catch (error) {
    result.errors.push((error as Error).message);

    return result;
  }

  const { video, objectUrl } = loaded;

  try {
    const duration = Number.isFinite(video.duration) ? video.duration : null;
    result.durationSec = duration;
    result.width = video.videoWidth || null;
    result.height = video.videoHeight || null;

    if (duration !== null && duration > profile.hardMaxDurationSec) {
      result.errors.push(
        `動画が ${Math.round(duration)} 秒あります。${profile.hardMaxDurationSec} 秒以内にしてください。`,
      );

      return result;
    }

    if (duration !== null && duration > profile.recommendedMaxDurationSec) {
      result.warnings.push(
        `動画が ${Math.round(duration)} 秒あります。${profile.durationNote}`,
      );
    }

    if (result.width && result.height) {
      const ratio = result.width / result.height;

      if (
        Math.abs(ratio - profile.aspectRatio) >
        profile.aspectRatio * ASPECT_RATIO_TOLERANCE
      ) {
        result.warnings.push(
          `縦横比が ${profile.aspectRatioLabel} ではありません（${result.width}×${result.height}）。${profile.aspectMismatchNote}推奨は ${profile.recommendedResolution.width}×${profile.recommendedResolution.height} です。`,
        );
      }
    }

    result.posterFile = await capturePoster(
      video,
      file.name.replace(/\.[^.]+$/, ''),
    );

    if (!result.posterFile) {
      result.warnings.push(
        'ポスター画像を自動生成できませんでした。公開するには画像を手動で設定してください。',
      );
    }
  } finally {
    URL.revokeObjectURL(objectUrl);
    video.removeAttribute('src');
    video.load();
  }

  return result;
}
